/**
 * Client asset management for DP Portal
 * Handles dynamic loading of client logos and CSS from kforms production
 */

import { config } from "../config/config";

/** Legacy kforms path used only for default CSS when a client bundle has no stylesheet. */
const FORMS_FALLBACK_CSS_CLIENT = "kavach";

/** Canonical marketing logo (footer “Powered by” fallback). */
const PRIVIGO_MARKETING_LOGO_URL = "https://privigo.ai/images/Logo.png";

export interface ClientAssets {
    logoUrl: string;
    fallbackLogoUrl: string;
    cssUrl: string;
    fallbackCssUrl: string;
}

/**
 * Get client asset URLs based on subdomain
 */
export const getClientAssets = (clientSubdomain: string): ClientAssets => {
    const baseUrl = config.forms.baseUrl.replace(/\/$/, "");
    return {
        logoUrl: `${baseUrl}/${clientSubdomain}/logo.png`,
        fallbackLogoUrl: PRIVIGO_MARKETING_LOGO_URL,
        cssUrl: `${baseUrl}/${clientSubdomain}/css/style.css`,
        fallbackCssUrl: `${baseUrl}/${FORMS_FALLBACK_CSS_CLIENT}/css/style.css`,
    };
};

/**
 * Load client CSS dynamically
 */
export const loadClientCSS = (clientSubdomain: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const { cssUrl, fallbackCssUrl } = getClientAssets(clientSubdomain);

        // Remove any existing client CSS
        const existingLink = document.getElementById('client-css');
        if (existingLink) {
            existingLink.remove();
        }

        const link = document.createElement('link');
        link.id = 'client-css';
        link.rel = 'stylesheet';
        link.href = cssUrl;

        link.onload = () => {
            console.log(`Loaded client CSS for ${clientSubdomain}`);
            resolve();
        };

        link.onerror = () => {
            console.warn(`Failed to load client CSS for ${clientSubdomain}, using fallback`);
            link.href = fallbackCssUrl;
            link.onload = () => resolve();
            link.onerror = () => reject(new Error('Failed to load fallback CSS'));
        };

        document.head.appendChild(link);
    });
};

/**
 * Unload client CSS
 */
export const unloadClientCSS = (): void => {
    const existingLink = document.getElementById('client-css');
    if (existingLink) {
        existingLink.remove();
    }
};
